import type { ConnectionTier } from '@/lib/streamSelection';

/**
 * Débits réellement produits par les enregistreurs, vidéo plus audio, en bits par seconde.
 * À tenir à jour si les paramètres d'encodage changent côté back.
 */
const STREAM_BITRATE_BPS: Record<ConnectionTier, number> = {
  high: 6_128_000, // P1080 : 6000 + 128
  medium: 3_128_000, // P720 : 3000 + 128
  low: 1_296_000, // P480 : 1200 + 96
  'very-low': 896_000, // P360 : 800 + 96
};

/**
 * Marge exigée au-dessus du débit du flux. Lire tout juste au débit nominal ne laisse aucune place
 * pour reconstituer le tampon après le moindre creux, et le spectateur gèle au premier accroc.
 */
export const BANDWIDTH_SAFETY_FACTOR = 1.4;

/** Nombre de segments à charger avant de croire l'estimation : avant, c'est la valeur par défaut. */
export const BANDWIDTH_MIN_SAMPLES = 3;

/**
 * Palier déduit du débit réellement observé sur les segments. Préférable à la latence, qui ne dit
 * rien de la capacité à soutenir un flux : un bon ping sur une 4G saturée reste une 4G saturée.
 */
export function tierFromBandwidth(bitsPerSecond: number): ConnectionTier {
  const canSustain = (tier: ConnectionTier): boolean =>
    bitsPerSecond >= STREAM_BITRATE_BPS[tier] * BANDWIDTH_SAFETY_FACTOR;
  if (canSustain('high')) return 'high';
  if (canSustain('medium')) return 'medium';
  if (canSustain('low')) return 'low';
  return 'very-low';
}

async function testLatency(url = 'https://www.google.com/favicon.ico'): Promise<number> {
  const start = performance.now();
  await fetch(url, { cache: 'no-store', mode: 'no-cors' });
  return performance.now() - start;
}

export function tierFromLatency(averageMs: number): ConnectionTier {
  if (averageMs < 100) return 'high';
  if (averageMs < 300) return 'medium';
  if (averageMs < 600) return 'low';
  return 'very-low';
}

const TIER_ORDER: ConnectionTier[] = ['very-low', 'low', 'medium', 'high'];

/** Négatif si `a` est moins bon que `b`. Sert à traiter montée et descente différemment. */
export function compareTiers(a: ConnectionTier, b: ConnectionTier): number {
  return TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b);
}

/** Badges connexion : 3 niveaux (fort/moyen/faible). very-low s'affiche comme « faible ». */
export type DisplayConnectionTier = 'high' | 'medium' | 'low';
export function toDisplayTier(tier: ConnectionTier): DisplayConnectionTier {
  return tier === 'very-low' ? 'low' : tier;
}

/**
 * Repli quand aucune mesure de débit n'est disponible : lecture native d'Apple, ou tout début de
 * lecture avant les premiers segments. Mesure une latence vers un domaine tiers, ce qui a deux
 * défaut connu : ce n'est pas du débit. Rend `null` quand aucune sonde n'aboutit, typiquement
 * derrière un bloqueur de publicité, pour que l'appelant conserve son palier plutôt que de supposer
 * le pire.
 */
export async function measureConnectionTier(): Promise<ConnectionTier | null> {
  const latencies: number[] = [];
  for (let i = 0; i < 3; i++) {
    try {
      latencies.push(await testLatency());
    } catch {
      // Un bloqueur de publicité fait échouer l'appel. Compter mille millisecondes classerait le
      // spectateur au plus bas pour tout le live : on préfère ne rien conclure.
    }
  }
  if (latencies.length === 0) return null;
  const average = latencies.reduce((sum, value) => sum + value, 0) / latencies.length;
  return tierFromLatency(average);
}
